import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function StructuralDrying101Page() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Structural Drying 101: Why Industrial Equipment Matters | Indoor-Restore';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      'Learn why professional structural drying with industrial equipment is essential after water damage in Merced, CA. Indoor-Restore explains the science behind proper drying and why consumer fans fall short.'
    );

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'structural drying, industrial drying equipment, water damage restoration, professional dehumidifiers, Merced CA water damage'
    );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Structural Drying 101: Why Industrial Equipment Matters",
      "description": "Consumer fans and dehumidifiers are no match for professional-grade drying equipment. Learn why proper structural drying is essential to a full restoration after water damage.",
      "image": "https://public.readdy.ai/ai/img_res/9e869b76a76eccbc1398e780e4c444b9.jpg",
      "datePublished": "2025-04-30T08:00:00-07:00",
      "dateModified": "2025-04-30T08:00:00-07:00",
      "author": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": "https://mercedwaterrestoration.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://mercedwaterrestoration.com/blog/structural-drying-101"
      }
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How long does structural drying take after water damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "With professional industrial equipment, most structural drying jobs in Merced, CA take 3–5 days. The exact timeline depends on the extent of water intrusion, the materials affected (drywall, hardwood, concrete), and ambient humidity levels. Without professional equipment, drying can take weeks — allowing mold to grow in the meantime."
          }
        },
        {
          "@type": "Question",
          "name": "Why can't I just use household fans to dry water damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Household fans only move surface air and cannot extract moisture trapped inside walls, subfloors, or structural cavities. Professional air movers create high-velocity airflow that penetrates building materials, while industrial dehumidifiers remove the evaporated moisture from the air. Without this combination, hidden moisture leads to mold growth, wood rot, and structural damage."
          }
        },
        {
          "@type": "Question",
          "name": "What equipment do professionals use for structural drying?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Professional restoration companies use a combination of high-velocity air movers (LGR dehumidifiers), low-grain refrigerant dehumidifiers, desiccant dehumidifiers for extreme conditions, thermal imaging cameras to detect hidden moisture, and moisture meters to verify drying progress. This equipment is calibrated to IICRC S500 standards for water damage restoration."
          }
        },
        {
          "@type": "Question",
          "name": "Does insurance cover professional structural drying in Merced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes — most homeowner's insurance policies in California cover professional structural drying as part of water damage restoration. The cost of industrial drying equipment is typically included in your restoration estimate. Indoor-Restore Services works directly with insurance companies to ensure all necessary drying costs are covered."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-24 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gray-900 font-medium">Structural Drying 101</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-6">
            <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Restoration Tips
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Structural Drying 101: Why Industrial Equipment Matters
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                April 30, 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                4 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                Merced, CA
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-xl overflow-hidden mb-10 shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=industrial%20air%20movers%20and%20commercial%20dehumidifiers%20arranged%20in%20water%20damaged%20room%20restoration%20process%20professional%20equipment%20drying%20hardwood%20floors%20bright%20interior%20clean&width=1200&height=630&seq=structural-drying-hero-v1&orientation=landscape"
              alt="Industrial air movers and dehumidifiers set up for structural drying after water damage"
              title="Professional structural drying equipment — industrial air movers and dehumidifiers"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>After water damage, what you can't see is often more dangerous than what you can.</strong> Moisture hidden inside walls, under flooring, and within structural cavities is the silent cause of mold growth, wood rot, and long-term structural failure. The only way to eliminate it is through <strong>professional structural drying</strong> — and that requires industrial-grade equipment that no hardware store can match.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, we respond to water damage emergencies across Merced, CA every day. One of the most common mistakes we see homeowners make is attempting to dry their homes with consumer fans and box-store dehumidifiers. In this guide, we'll explain exactly why that approach fails — and what professional structural drying actually involves.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-water-flash-line text-teal-600"></i>
              Where Does Water Actually Go After a Flood?
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              When water enters your home — whether from a burst pipe, appliance leak, or storm flooding — it doesn't just sit on the surface. Water is absorbed rapidly by porous building materials:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Drywall</strong> absorbs water like a sponge and begins deteriorating within hours</li>
              <li><strong>Hardwood and engineered flooring</strong> warp and buckle as moisture penetrates the grain</li>
              <li><strong>Subfloor materials</strong> (OSB, plywood) swell and delaminate when saturated</li>
              <li><strong>Wall insulation</strong> traps moisture and becomes a breeding ground for mold</li>
              <li><strong>Structural framing</strong> (studs, joists) can begin to rot if not dried within 48–72 hours</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              The challenge is that much of this moisture is <strong>invisible to the naked eye</strong>. A wall may look dry on the surface while harboring significant moisture content inside. This is why professional structural drying relies on moisture meters and thermal imaging cameras — not visual inspection alone.
            </p>

            {/* Callout */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-5 mb-8">
              <p className="text-amber-800 text-sm font-semibold flex items-start gap-2">
                <i className="ri-alarm-warning-line text-lg mt-0.5 shrink-0"></i>
                <span><strong>Critical Window:</strong> Mold can begin growing in as little as 24–48 hours after water exposure. Structural drying must begin immediately to prevent secondary damage that can double or triple your restoration costs.</span>
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-close-circle-line text-teal-600"></i>
              Why Consumer Equipment Falls Short
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              It's tempting to grab a few box fans and a dehumidifier from the hardware store. But here's the reality: <strong>consumer-grade equipment is simply not designed for structural drying</strong>. Here's why it fails:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-red-50 rounded-lg p-5 border border-red-100">
                <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <i className="ri-close-line"></i> Box Fans
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Move surface air only. Cannot penetrate walls, subfloors, or cavities. Simply push humid air around the room without extracting moisture from materials.
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-5 border border-red-100">
                <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <i className="ri-close-line"></i> Retail Dehumidifiers
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Rated for normal humidity control, not water damage restoration. Typically remove 30–70 pints/day — far below the 150–200+ pints/day needed for structural drying.
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-5 border border-red-100">
                <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <i className="ri-close-line"></i> No Moisture Monitoring
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Without moisture meters, you have no way to verify that materials have reached safe drying standards. You may stop too early, leaving hidden moisture behind.
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-5 border border-red-100">
                <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <i className="ri-close-line"></i> No Psychrometric Control
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Professional drying requires balancing temperature, humidity, and airflow in a precise ratio. Consumer equipment cannot achieve or maintain these conditions.
                </p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              The result? Homes dried with consumer equipment often appear dry on the surface while harboring dangerous moisture levels inside walls and floors. Weeks later, homeowners discover mold colonies, warped flooring, and musty odors — problems that are far more expensive to fix than the original water damage.
            </p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-tools-line text-teal-600"></i>
              The Professional Structural Drying Process
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Professional structural drying is a science, not just a matter of pointing fans at wet floors. At Indoor-Restore Services, we follow the <strong>IICRC S500 Standard for Professional Water Damage Restoration</strong> — the industry benchmark for safe, effective drying. Here's what that process looks like:
            </p>

            <div className="space-y-5 mb-8">
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">1</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Moisture Mapping</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We use <strong>thermal imaging cameras</strong> and <strong>calibrated moisture meters</strong> to map every affected area — including hidden cavities behind walls and under floors. This creates a baseline for measuring drying progress.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">2</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Strategic Equipment Placement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    <strong>High-velocity air movers</strong> are positioned at precise angles to create a vortex of airflow that draws moisture out of building materials. Equipment placement follows psychrometric calculations — not guesswork.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">3</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Industrial Dehumidification</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    <strong>LGR (Low Grain Refrigerant) dehumidifiers</strong> extract the evaporated moisture from the air at rates of 150–200+ pints per day. In extreme cases, we deploy <strong>desiccant dehumidifiers</strong> that work effectively even in cold or very humid conditions.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">4</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Daily Monitoring & Adjustment</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Our technicians return daily to <strong>measure moisture readings</strong>, adjust equipment placement, and document progress. Drying is not complete until all materials reach their target moisture content — typically below 16% for wood and below 1% for concrete.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">5</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Final Clearance Documentation</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Once drying goals are met, we provide a <strong>written drying report</strong> with all moisture readings — documentation your insurance company requires and that protects you from future mold claims.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-settings-3-line text-teal-600"></i>
              The Equipment That Makes the Difference
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Professional structural drying relies on a specific combination of equipment working together as a system. Here's what Indoor-Restore Services deploys on every job:
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3">
                    <i className="ri-wind-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">High-Velocity Air Movers</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Move 1,500–3,000 CFM of air to accelerate evaporation from structural materials
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3">
                    <i className="ri-drop-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">LGR Dehumidifiers</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Remove 150–200+ pints of moisture per day, far exceeding consumer-grade units
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3">
                    <i className="ri-scan-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Thermal Imaging Cameras</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Detect hidden moisture behind walls and under floors invisible to the naked eye
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-calendar-check-line text-teal-600"></i>
              How Long Does Structural Drying Take?
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              With professional industrial equipment, most structural drying jobs in Merced, CA are completed in <strong>3–5 days</strong>. The timeline depends on several factors:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Extent of water intrusion</strong> — a small appliance leak vs. a flooded basement</li>
              <li><strong>Materials affected</strong> — concrete dries slower than drywall; hardwood requires careful monitoring</li>
              <li><strong>Ambient conditions</strong> — Merced's warm summers can accelerate drying; winter humidity can slow it</li>
              <li><strong>Whether structural cavities were opened</strong> — sometimes drywall must be removed to dry wall cavities effectively</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Without professional equipment, the same job can take <strong>2–4 weeks</strong> — well past the 48-hour window when mold begins to colonize. By that point, what started as a water damage claim has become a mold remediation project, significantly increasing costs and health risks.
            </p>

            {/* Section 6 - FAQ */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-question-line text-teal-600"></i>
              Frequently Asked Questions
            </h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-base font-bold text-gray-900 mb-2">How long does structural drying take after water damage?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                With professional industrial equipment, most structural drying jobs in Merced, CA take 3–5 days. Without professional equipment, drying can take weeks — allowing mold to grow in the meantime.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-base font-bold text-gray-900 mb-2">Why can't I just use household fans to dry water damage?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Household fans only move surface air and cannot extract moisture trapped inside walls, subfloors, or structural cavities. Professional air movers create high-velocity airflow that penetrates building materials, while industrial dehumidifiers remove the evaporated moisture from the air.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-base font-bold text-gray-900 mb-2">What equipment do professionals use for structural drying?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Professional restoration companies use high-velocity air movers, LGR dehumidifiers (150–200+ pints/day), desiccant dehumidifiers for extreme conditions, thermal imaging cameras to detect hidden moisture, and moisture meters to verify drying progress — all calibrated to IICRC S500 standards.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-base font-bold text-gray-900 mb-2">Does insurance cover professional structural drying in Merced?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Yes — most homeowner's insurance policies in California cover professional structural drying as part of water damage restoration. Indoor-Restore Services works directly with insurance companies to ensure all necessary drying costs are covered.
              </p>
            </div>

            {/* Closing */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-shield-star-line text-teal-600"></i>
              Don't Leave Moisture to Chance
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Structural drying is the foundation of every successful water damage restoration. Without it, even the most thorough cleanup leaves behind hidden moisture that will cause problems for months or years to come. <strong>Industrial equipment isn't a luxury — it's a necessity.</strong>
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, our IICRC-certified technicians bring the right equipment, the right training, and the right documentation to every job in Merced, CA. We don't consider a job done until our moisture meters confirm every affected material has reached its target drying standard.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong>Dealing with water damage right now?</strong> Call our 24/7 emergency line at{' '}
              <a href="tel:2094512288" className="text-teal-600 font-semibold hover:text-teal-700 underline">
                (209) 451-2288
              </a>{' '}
              — we'll dispatch a team immediately and start the structural drying process before hidden moisture becomes a bigger problem.
            </p>

          </div>
        </div>
      </article>

      {/* Emergency CTA */}
      <section className="bg-teal-700 py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Need Professional Structural Drying in Merced?</h3>
          <p className="text-teal-100 mb-6 text-sm">
            Our IICRC-certified team deploys industrial drying equipment 24/7 across Merced County.
          </p>
          <a
            href="tel:2094512288"
            className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Call 24/7: (209) 451-2288
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link
              to="/blog/water-damage-first-24-hours"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=flooded%20living%20room%20interior%20with%20water%20on%20hardwood%20floor%20furniture%20damaged%20homeowner%20assessing%20water%20damage%20natural%20light%20realistic%20photography&width=400&height=250&seq=blog1-indoor&orientation=landscape"
                  alt="What to Do in the First 24 Hours After Water Damage"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Water Damage</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  What to Do in the First 24 Hours After Water Damage
                </h4>
              </div>
            </Link>

            <Link
              to="/blog/prevent-mold-after-flood"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20mold%20remediation%20specialist%20in%20protective%20gear%20inspecting%20wall%20with%20moisture%20meter%20in%20residential%20home%20clean%20bright%20interior&width=400&height=250&seq=blog2-indoor&orientation=landscape"
                  alt="How to Prevent Mold After a Flood"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Mold Prevention</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  How to Prevent Mold After a Flood: A Complete Guide
                </h4>
              </div>
            </Link>

            <Link
              to="/blog/homeowners-insurance-claim-water-damage"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=insurance%20agent%20and%20homeowner%20reviewing%20documents%20at%20kitchen%20table%20after%20water%20damage%20claim%20paperwork%20professional%20setting%20warm%20lighting&width=400&height=250&seq=blog3-indoor&orientation=landscape"
                  alt="Navigating Your Homeowner's Insurance Claim for Water Damage"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Insurance Tips</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  Navigating Your Homeowner's Insurance Claim for Water Damage
                </h4>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
